// React
import classnames from 'classnames';
import 'cleave.js/dist/addons/cleave-phone.in';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';

// Yup validation
import { useFormik } from 'formik';

// Helpers Injection
import { APIClient } from '../../helpers/helpersInjection';

// Containers Injection
import { useLoading } from '../../../../hook/useLoading';
import { AssociatesInjection } from '../../containers/associatesInjection';
import {
  PageWrapper,
  LoadingScreen,
} from '../../containers/componentsInjection';
import CardNotes from './CardNotesAssociates';
import { CardProposalsAssociates } from './CardProposalsAssociates';

// assets
import user_pick from '../../../../assets/images/users/user-pic.jpg';
import { ViewAssociatesDataRegister } from '../../../../components/viewAssociatesDataRegister';
import { associatesAndProposalsValidation } from '../../../../utils/validation/associatesAndProposalValidation';

const AssociatesEditScreen = () => {
  const [image, setImage] = useState(null);
  const [associate, setAssociate] = useState(null);

  const [activeTab, setActiveTab] = useState('1');
  const [goToProposals, setGoToProposals] = useState(false);

  const [loading, setLoading] = useState(true);
  const [isUpdate, setIsUpdate] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [disableCPF, setDisableCPF] = useState(false);

  const api = new APIClient();
  const history = useHistory();
  const { loadingWithTime } = useLoading();

  const {
    createAssociate,
    updateAssociate,
    associates,
    requestGetProposalsAssociates,
    proposalsAssociates,
    reqStatus,
  } = AssociatesInjection();

  const findAssociate = async (id) => {
    await api
      .get(`/customer/${id}`)
      .then((associateFind) => {
        if (associateFind) {
          setAssociate(associateFind);
          setIsUpdate(true);
          setLoading(false);
          requestGetProposalsAssociates(associateFind?.hashid);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      nameInput: associate ? associate.name : '',
      cpfInput: associate ? associate.cpf : '',
      rgInput: associate ? associate.rg : '',
      orgaoInput: associate ? associate.rg_issuer : '',
      birthdayInput: associate ? associate.birthdate : '',
      genderInput: associate && associate.gender,
      stateCivilInput: associate
        ? {
            value: associate.marital_status_id,
            label: associate.marital_status_name,
          }
        : {},
      jobInput: associate ? associate.profession : '',
      obsInput: associate ? associate.obs : '',
      emailInput: associate ? associate.email : '',
      phoneInput: associate ? associate.phones.phone1 : '',
      telInput: associate ? associate.phones.phone2 : '',
      whatsappInput: associate ? associate.phones.whatsapp : '',
      canSendMessageSelect: associate ? associate.accept_messages : true,
      zipInput: associate ? associate.zipcode : '',
      streetInput: associate ? associate.address1 : '',
      numberInput: associate ? associate.address_number : '',
      address2Input: associate ? associate.address2 : '',
      neighborhoodInput: associate ? associate.district : '',
      cityInput: associate
        ? { value: associate.city_id, label: associate.city }
        : '',
      pointInput: associate ? associate.reference_point : '',
      isActiveSelect: associate ? associate.active : true,
      isDiedSelect: associate ? associate.deceased : false,
      motherName: associate ? associate.mother_name : '',
    },

    validationSchema: associatesAndProposalsValidation,

    onSubmit: (values) => {
      const valuesToSubmit = {
        name: values.nameInput || null,
        cpf: values.cpfInput || null,
        rg: values.rgInput || null,
        rg_issuer: values.orgaoInput || null,
        marital_status_id: values.stateCivilInput.value || null,
        birthdate: values.birthdayInput || null,
        gender: values.genderInput || null,
        profession: values.jobInput || null,
        email: values.emailInput || null,
        address1: values.streetInput || null,
        address2: values.address2Input || null,
        address_number: values.numberInput || null,
        reference_point: values.pointInput || null,
        district: values.neighborhoodInput || null,
        city_id: values.cityInput.value || null,
        zipcode: values.zipInput || null,
        obs: values.obsInput || null,
        photo: image || undefined,
        deceased: values.isDiedSelect,
        active: values.isActiveSelect,
        accept_messages: values.canSendMessageSelect,
        phone1: values.phoneInput || null,
        phone2: values.telInput || null,
        whatsapp: values.whatsappInput || null,
        mother_name: values.motherName || null,
      };

      try {
        loadingWithTime(1000);
        if (associate) {
          updateAssociate(associate.id, valuesToSubmit, history);
        } else {
          createAssociate(valuesToSubmit, goToProposals, history);
          history.goBack();
        }
      } catch {
        setButtonLoading(false);
      }
    },
  });

  function getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  const getParams = useCallback(() => {
    const url = new URL(window.location);
    const cpf = url.searchParams.get('cpf');

    return {
      cpf,
    };
  }, []);

  const getValues = useCallback(() => {
    const params = getParams();

    if (params.cpf) {
      setDisableCPF(true);
      validation.setValues({
        ...validation.values,
        cpfInput: params.cpf,
      });
    }
  }, [getParams, validation]);

  const tabChange = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  useEffect(() => {
    const full_url = document.URL; // Get current url
    const url_array = full_url.split('/'); // Split the string into an array with / as separator
    const last_segment = url_array[url_array.length - 1];
    findAssociate(last_segment);
    getValues();
    if (last_segment === 'proposal') {
      setGoToProposals(true);
    }
  }, [associates]);

  return (
    <LoadingScreen
      loading={loading}
      titleMetaTag={!isUpdate ? 'Novo Associado' : 'Editar Associado'}
    >
      <PageWrapper
        title={!isUpdate ? 'Adicionar Associado' : 'Editar Associado'}
      >
        <Row>
          {isUpdate && (
            <Col xxl={3}>
              <Card>
                <CardBody className="p-4">
                  <div className="text-center">
                    <div className="profile-user position-relative d-inline-block mx-auto  mb-4">
                      {}
                      <img
                        src={
                          image
                            ? image
                            : associate.photo
                            ? associate.photo
                            : user_pick
                        }
                        className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                        alt="user-profile"
                      />
                      <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                        <Input
                          id="profile-img-file-input"
                          type="file"
                          className="profile-img-file-input"
                          onChange={(e) => {
                            if (
                              e.target.files[0].type === 'image/jpeg' ||
                              e.target.files[0].type === 'image/jpg' ||
                              e.target.files[0].type === 'image/png'
                            ) {
                              getBase64(e.target.files[0]).then((data) => {
                                setImage(data);
                              });
                            } else {
                              alert('Formato inválido');
                            }
                          }}
                        />
                        <Label
                          htmlFor="profile-img-file-input"
                          className="profile-photo-edit avatar-xs"
                        >
                          <span className="avatar-title rounded-circle bg-light text-body">
                            <i className="ri-camera-fill"></i>
                          </span>
                        </Label>
                      </div>
                    </div>
                    <h5 className="fs-16 mb-1">{associate.name || ''}</h5>
                    <p className="text-muted mb-0">{associate.email || ''}</p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}

          <Col xxl={isUpdate ? 9 : 12}>
            <Card>
              <CardHeader>
                <Nav
                  className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                  role="tablist"
                >
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === '1' })}
                      onClick={() => {
                        tabChange('1');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="fas fa-home"></i>
                      Dados Cadastrais
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      to="#"
                      className={classnames({ active: activeTab === '2' })}
                      disabled={!isUpdate}
                      onClick={() => {
                        tabChange('2');
                      }}
                      type="button"
                    >
                      <i className="far fa-user"></i>
                      Notas
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      to="#"
                      className={classnames({ active: activeTab === '3' })}
                      disabled={!isUpdate}
                      onClick={() => {
                        tabChange('3');
                      }}
                      type="button"
                    >
                      <i className="far fa-user"></i>
                      Propostas
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardHeader>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  <ViewAssociatesDataRegister
                    validation={validation}
                    buttonLoading={buttonLoading}
                    history={history}
                    disabledCPF={disableCPF}
                  >
                    <CardFooter className="mt-3">
                      <Col md={12}>
                        <div className="d-flex justify-content-between align-items-center ">
                          <span className="card-text">
                            <small className="text-muted">
                              {associate?.last_update}
                            </small>
                          </span>
                          <div className="text-end">
                            <button
                              type="button"
                              className="btn btn-link me-3"
                              onClick={() => {
                                history.goBack();
                              }}
                            >
                              Cancelar
                            </button>
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={buttonLoading}
                            >
                              Salvar
                            </button>
                          </div>
                        </div>
                      </Col>
                    </CardFooter>
                  </ViewAssociatesDataRegister>
                </TabPane>

                <TabPane tabId="2">
                  <CardBody className="mt-3">
                    <CardNotes customer={associate} />
                  </CardBody>
                </TabPane>

                <TabPane tabId="3">
                  <CardBody className="mt-3">
                    <CardProposalsAssociates
                      history={history}
                      customer={associate}
                      isLoading={reqStatus}
                      proposalsAssociates={proposalsAssociates}
                    />
                  </CardBody>
                </TabPane>
              </TabContent>
            </Card>
          </Col>
        </Row>
      </PageWrapper>
    </LoadingScreen>
  );
};
export default AssociatesEditScreen;
